import type { ReactNode } from 'react'
import { useFieldContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import { InputGroup, type InputGroupProps } from '../ui/input-group'
import { Label } from '../ui/label'
import FieldInfo from './FieldInfo'
import { Typography } from '@/components/ui/typography'
import { useIsReadOnly } from '@/lib/casl'

export type UrlInputProps = InputGroupProps & {
  label?: ReactNode
  labelEnd?: ReactNode
  readOnlyEmptyText?: ReactNode
}

export default function UrlInput({
  id,
  label,
  type,
  labelEnd,
  placeholder,
  className,
  readOnlyEmptyText = 'No URL provided',
  ...props
}: UrlInputProps) {
  const field = useFieldContext<string | null | undefined>()
  const { isReadOnly } = useIsReadOnly()

  return (
    <div className="flex flex-col gap-y-2 w-full">
      {!!label && (
        <div className="inline-flex items-center">
          <Label htmlFor={id || field.name}>{label}</Label>
          {labelEnd}
        </div>
      )}

      {isReadOnly ? (
        <Typography variant="label" tone={!field.state.value ? 'muted' : 'default'}>
          {field.state.value ? `https://${field.state.value}` : readOnlyEmptyText}
        </Typography>
      ) : (
        <InputGroup
          id={id || field.name}
          type="text"
          placeholder={placeholder}
          value={field.state.value ?? ''}
          onChange={(e) => field.handleChange((e.target.value || '').toLowerCase())}
          onBlur={() => field.handleBlur()}
          startAdornment={<span>https://</span>}
          inputClassName={cn('w-full pl-[calc(0.625rem+3.77em)]', className)}
          {...props}
        />
      )}

      {type !== 'hidden' && <FieldInfo />}
    </div>
  )
}
